import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

/**
 * A card that hangs off the document rather than off whatever opened it.
 *
 * `position: fixed` is *not* viewport-relative if any ancestor has a transform,
 * a filter or a perspective — that ancestor becomes the containing block
 * instead. Every popover in this app is opened from inside a table row, and
 * `.rank-row` carries `animation: rank-in ... both`, which leaves a settled
 * computed transform of `matrix(1, 0, 0, 1, 0, 0)`.
 *
 * The keyframe's `to` says `transform: none`, which would be harmless. The
 * *computed* value after a filled animation is an identity matrix, which is not
 * `none` — and an identity matrix establishes a containing block exactly like
 * any other. So every card was positioned relative to its own row, and appeared
 * further down the page the further down the row was. **Measured**: a trigger
 * at y=520 opened a card at y=1057, a drift of 516px, which is precisely that
 * row's offset from the top of the window.
 *
 * Fixing the keyframe would fix today's instance and leave the trap armed for
 * the next element that animates. A portal is immune to all of it: the card is
 * a child of `<body>`, so no ancestor of the trigger can be its containing
 * block, whatever it grows later.
 *
 * Not `<dialog>` and no focus trap: these open on hover, they contain nothing to
 * interact with, and stealing focus from the row a keyboard user is on would be
 * the opposite of helpful.
 */

export function Floating({ children }: { readonly children: ReactNode }) {
  /*
   * Straight to `<body>`. A dedicated container element would need mounting,
   * cleaning up, and a decision about what happens when two cards want it —
   * and `<body>` needs none of that, because React removes the portal's nodes
   * when the component that rendered it unmounts.
   */
  return createPortal(children, document.body);
}

/** Breathing room a card keeps from the edges of the window. */
const EDGE = 12;

/**
 * Keeps a floating card inside the window.
 *
 * Takes where the trigger would like the card and returns where it actually
 * fits, measured after the card has rendered — its size is not knowable before
 * that, and guessing it is how a card ends up half off-screen on the one row
 * near the bottom.
 *
 * `max` last on both axes, so a card larger than the window pins to the
 * top-left and is at least readable from its start rather than sitting at a
 * negative offset with its opening words off-screen.
 */
export function useFitted(anchor: {
  top: number;
  left: number;
}): readonly [RefObject<HTMLDivElement | null>, { top: number; left: number }] {
  const node = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(anchor);

  useLayoutEffect(() => {
    const element = node.current;
    if (element === null) return;

    const { offsetHeight: height, offsetWidth: width } = element;
    setAt({
      top: Math.max(EDGE, Math.min(anchor.top, window.innerHeight - EDGE - height)),
      left: Math.max(EDGE, Math.min(anchor.left, window.innerWidth - EDGE - width)),
    });
  }, [anchor]);

  return [node, at];
}
